/**
 * Utility functions for saving and loading ceSDK scene strings to/from IndexedDB.
 * Used for MVP implementation to persist design file edits without backend changes.
 * Includes metadata tracking for validation and user feedback.
 */

export interface SceneMetadata {
  assetId: string;
  assetType: "image" | "video";
  sourceUrl: string;
  updatedAt: number;
  ceSDKVersion?: string;
}

interface StoredSceneRecord {
  assetId: string;
  scene: string;
  metadata: SceneMetadata;
}

const DB_NAME = "cesdk_scenes_db";
const DB_VERSION = 1;
const STORE_NAME = "scenes";

let dbInstance: IDBDatabase | null = null;

/**
 * Initialize IndexedDB database
 * @returns Promise resolving to the database instance
 */
async function initDatabase(): Promise<IDBDatabase> {
  if (dbInstance) {
    return dbInstance;
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    // eslint-disable-next-line unicorn/prefer-add-event-listener
    request.onerror = () => {
      console.error("Failed to open IndexedDB:", request.error);
      reject(request.error);
    };

    // eslint-disable-next-line unicorn/prefer-add-event-listener
    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "assetId" });
        store.createIndex("updatedAt", "metadata.updatedAt", { unique: false });
      }
    };
  });
}

/**
 * Save scene string to IndexedDB with metadata
 * @param assetId - The asset ID to use as the storage key
 * @param sceneString - The scene string to save
 * @param metadata - Metadata about the scene
 */
export async function saveScene(
  assetId: string,
  sceneString: string,
  metadata: SceneMetadata
): Promise<void> {
  try {
    const db = await initDatabase();
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    const storedScene: StoredSceneRecord = {
      assetId,
      scene: sceneString,
      metadata: {
        ...metadata,
        updatedAt: Date.now(),
      },
    };

    await new Promise<void>((resolve, reject) => {
      const request = store.put(storedScene);
      // eslint-disable-next-line unicorn/prefer-add-event-listener
      request.onsuccess = () => resolve();
      // eslint-disable-next-line unicorn/prefer-add-event-listener
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("Failed to save scene to IndexedDB:", error);
    // Handle errors gracefully - don't throw
  }
}

/**
 * Load scene string from IndexedDB for a given asset ID
 * @param assetId - The asset ID to look up
 * @returns The scene string and metadata if found, null otherwise
 */
export async function loadScene(
  assetId: string
): Promise<{ scene: string; metadata: SceneMetadata } | null> {
  try {
    const db = await initDatabase();
    const transaction = db.transaction([STORE_NAME], "readonly");
    const store = transaction.objectStore(STORE_NAME);

    const storedRecord = await new Promise<StoredSceneRecord | undefined>(
      (resolve, reject) => {
        const request = store.get(assetId);
        // eslint-disable-next-line unicorn/prefer-add-event-listener
        request.onsuccess = () => resolve(request.result);
        // eslint-disable-next-line unicorn/prefer-add-event-listener
        request.onerror = () => reject(request.error);
      }
    );

    if (!storedRecord) {
      return null;
    }

    return {
      scene: storedRecord.scene,
      metadata: storedRecord.metadata,
    };
  } catch (error) {
    console.error("Failed to load scene from IndexedDB:", error);
    return null;
  }
}

/**
 * Check if a scene string exists for a given asset ID
 * @param assetId - The asset ID to check
 * @returns True if a scene string exists, false otherwise
 */
export async function hasScene(assetId: string): Promise<boolean> {
  try {
    const db = await initDatabase();
    const transaction = db.transaction([STORE_NAME], "readonly");
    const store = transaction.objectStore(STORE_NAME);

    const result = await new Promise<boolean>((resolve, reject) => {
      const request = store.get(assetId);
      // eslint-disable-next-line unicorn/prefer-add-event-listener
      request.onsuccess = () => resolve(!!request.result);
      // eslint-disable-next-line unicorn/prefer-add-event-listener
      request.onerror = () => reject(request.error);
    });

    return result;
  } catch (error) {
    console.error("Failed to check scene in IndexedDB:", error);
    return false;
  }
}

/**
 * Clear scene string from IndexedDB for a given asset ID
 * @param assetId - The asset ID to clear
 */
export async function clearScene(assetId: string): Promise<void> {
  try {
    const db = await initDatabase();
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    await new Promise<void>((resolve, reject) => {
      const request = store.delete(assetId);
      // eslint-disable-next-line unicorn/prefer-add-event-listener
      request.onsuccess = () => resolve();
      // eslint-disable-next-line unicorn/prefer-add-event-listener
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("Failed to clear scene from IndexedDB:", error);
  }
}

/**
 * Migrate scene from old asset ID to new asset ID
 * Used when an asset is replaced and gets a new ID
 * @param oldAssetId - The original asset ID
 * @param newAssetId - The new asset ID
 * @param newAssetUrl - The new asset URL
 */
export async function migrateScene(
  oldAssetId: string,
  newAssetId: string,
  newAssetUrl: string
): Promise<void> {
  try {
    const oldScene = await loadScene(oldAssetId);
    if (oldScene) {
      // Update metadata with new asset ID and URL
      const newMetadata: SceneMetadata = {
        ...oldScene.metadata,
        assetId: newAssetId,
        sourceUrl: newAssetUrl,
        updatedAt: Date.now(),
      };
      // Save with new asset ID
      await saveScene(newAssetId, oldScene.scene, newMetadata);
      // Clear old scene
      await clearScene(oldAssetId);
      console.log(`Scene migrated from ${oldAssetId} to ${newAssetId}`);
    }
  } catch (error) {
    console.error("Failed to migrate scene:", error);
  }
}

/**
 * Get formatted "last saved" time string (e.g., "2m ago", "1h ago")
 * @param assetId - The asset ID to check
 * @returns Formatted time string or null if no scene exists
 */
export async function getLastSavedTime(
  assetId: string
): Promise<string | null> {
  try {
    const loaded = await loadScene(assetId);
    if (!loaded) {
      return null;
    }

    const now = Date.now();
    const updatedAt = loaded.metadata.updatedAt;
    const diffMs = now - updatedAt;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) {
      return "just now";
    } else if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      // For older saves, show date
      const date = new Date(updatedAt);
      return date.toLocaleDateString();
    }
  } catch (error) {
    console.error("Failed to get last saved time:", error);
    return null;
  }
}
